import { Dispatch, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { UnknownAction } from '@reduxjs/toolkit';

import { toNumber } from 'lodash';
import { getGlobalDateOptions, MSATL4RasterColorMaps, TARGET_GROUP_LOOKUP_BY_CHILD } from '../../../consts';
import {
    selectPlatform,
    selectUrlParamsInitialized,
    setAreaEmissionsProduct,
    setEmissionsDrawerCardData,
    setEmissionsMapViewState,
    setGlobalDate,
    setL4MSATColorScale,
    setPlatform,
    setTargetDate,
    setUrlParamsInitialized
} from '../../../reducers';
import {
    AreaEmissionsProducts,
    CardUrlState,
    Platforms,
    UrlState,
    UrlViewState,
    ViewStatePropsQueryString
} from '../../../types';
import { buildSearchObject, getDayEnd, parseDateString, parseQueryString, setInLocalStorage } from '../../../utils';
import {
    QueryStringObject,
    validateL4MSATColorScaleParam,
    validateUrlDateString,
    validateUrlGlobalDateString,
    validateUrlPlatformParam,
    validateUrlProductParam,
    validateUrlViewState
} from '../../../utils/urlValidators';

/**
 * Custom hook to keep the url querystring in sync with specific pieces of app state.
 * Updates the url
 * Accepts a list of object defining which keys & values the hook should add to the querystring
 * and how those key/value pairs should be parsed on page load.
 */
export function useSyncQueryString({
    urlStateItems,
    dispatch,
    urlUpdateFn
}: {
    urlStateItems: UrlState;
    dispatch: Dispatch<UnknownAction>;
    urlUpdateFn: (q: QueryStringObject) => void;
}) {
    const urlParamsInitialized = useSelector(selectUrlParamsInitialized);
    const platform = useSelector(selectPlatform);
    /**
     * Handles the initial querystring on page load:
     * 1) extracts state information from the querystring
     * 2) dispatches the associated actions to the store
     */
    useEffect(() => {
        const { search } = window.location;
        const initialQueryStringObject = parseQueryString(search);
        let newGlobalDate = '';
        let newDateAsNumber: number | null = null;
        let newPlatform: Platforms | null = null;
        let newProduct: AreaEmissionsProducts | null = null;
        let newColorScale: MSATL4RasterColorMaps | null = null;
        const newTargetDates: { [targetId: string]: number } = {};

        let viewState: UrlViewState = {} as UrlViewState;
        const emissionsDrawerCardData: CardUrlState = {} as CardUrlState;

        Object.entries(initialQueryStringObject).forEach(([key, newValue]) => {
            // if the key isn't in the querystring or has no value, do nothing
            if (
                (!newValue && typeof newValue !== 'number') ||
                (typeof newValue === 'object' && Object.keys(newValue).length === 0)
            )
                return;

            if (key === 'platform' && typeof newValue === 'string') {
                if (validateUrlPlatformParam(newValue)) {
                    newPlatform = newValue;
                    dispatch(setPlatform(newPlatform));
                }
            } else if (key === 'date' && typeof newValue === 'string') {
                const isValidDate = validateUrlDateString(newValue);
                if (isValidDate) {
                    const date = parseDateString(newValue);
                    // Set the timestamp to the end of the day to
                    // be in sync with all other end-of-period dates in the app
                    const endOfDay = date && getDayEnd(date);
                    newDateAsNumber = endOfDay && endOfDay.getTime();
                    newGlobalDate = newValue;
                }
            } else if (key === 'product' && typeof newValue === 'string') {
                if (validateUrlProductParam(newValue)) {
                    newProduct = newValue;
                    dispatch(setAreaEmissionsProduct(newProduct));
                }
            } else if (key === 'color-scale' && typeof newValue === 'string') {
                if (validateL4MSATColorScaleParam(newValue)) {
                    newColorScale = newValue;
                    dispatch(setL4MSATColorScale(newColorScale));
                }
            } else if (key.includes('view-')) {
                const viewKey: keyof UrlViewState = key.split('view-')[1] as keyof UrlViewState;
                viewState = { ...viewState, [viewKey]: newValue };
            } else if ((key.includes('select') || key.includes('target')) && typeof newValue === 'string') {
                if (key === 'targets') {
                    // list of targets should be split into an array of strings
                    emissionsDrawerCardData[key] = newValue.split(',');
                } else if (key.includes('points')) {
                    // list of points should be split to an array of numbers
                    emissionsDrawerCardData[key] = newValue.split(',');
                } else if (key === 'selected-lat' || key === 'selected-lng') {
                    emissionsDrawerCardData[key] = toNumber(newValue);
                } else {
                    emissionsDrawerCardData[key] = newValue;
                    if (key.includes('target') && key.includes('date')) {
                        const targetId = key.replace(/(^target-)(.*)(-date$)/, '$2');
                        const targetDate = getDayEnd(new Date(newValue)).getTime();
                        const targetGroupId = TARGET_GROUP_LOOKUP_BY_CHILD[targetId];
                        newTargetDates[targetGroupId || targetId] = targetDate;
                    }
                }
            }
        });

        if (validateUrlViewState(viewState)) dispatch(setEmissionsMapViewState(viewState as ViewStatePropsQueryString));
        // Validate the date against `newPlatform`, or use the current `platform` in the Redux store as
        // a fallback if not provided
        if (
            newDateAsNumber &&
            validateUrlGlobalDateString(newGlobalDate, getGlobalDateOptions(newPlatform || platform))
        ) {
            dispatch(setGlobalDate(newDateAsNumber));
        }
        if (Object.keys(emissionsDrawerCardData)) {
            dispatch(setEmissionsDrawerCardData(emissionsDrawerCardData));
            setInLocalStorage('introDismissed', true);
        }

        // set target dates
        Object.entries(newTargetDates).forEach(([targetId, date]) => {
            dispatch(setTargetDate({ date, targetId }));
        });

        dispatch(setUrlParamsInitialized());

        // Run once on mount to initialize state from the URL.
        // Dependencies like urlStateItems/platform are partially or fully derived from
        // this effect itself and would cause infinite loops if added.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // update query string on state changes
    useEffect(() => {
        let newQueryObj = {};
        if (urlParamsInitialized) {
            // each time any dependency changes, get the current state of all dependencies
            // prevents missing some state updates due to throttled router.replace
            Object.entries(urlStateItems).forEach(([key, value]) => {
                const newSearchParams = buildSearchObject(key, value);
                newQueryObj = {
                    ...newQueryObj,
                    ...newSearchParams
                };
            });
            urlUpdateFn(newQueryObj);
        }
    }, [urlStateItems, urlParamsInitialized, urlUpdateFn]);
}
